/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component } from '@angular/core';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { MasterdataService } from '../../../services/masterdata.service';
import {
  AddUserInterestTagsRequest,
  SeasonalUserInterestTags,
} from '../../../models/masterDataResponse';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import {
  AnyoS3Buckets,
  FileUtilsService,
} from 'projects/app-core/src/service/fileService';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { v4 as uuidv4 } from 'uuid';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import { PartnerService } from '../../../services/partner.service';
import { PartnerGridRequest } from '../../../models/partnerGridRequest';
import { ExpertsService } from '../../../services/experts.service';
import { ExpertsGridRequest } from '../../../models/expertsGridRequest';

interface DropdownOptions {
  value: string;
  label: string;
}
@Component({
  selector: 'app-manage-seasonal-user-interest',
  templateUrl: './manage-seasonal-user-interest.component.html',
  styleUrl: './manage-seasonal-user-interest.component.scss',
})
export class ManageSeasonalUserInterestComponent extends AnyoComponent {
  constructor(
    authService: AuthService,
    private masterDataService: MasterdataService,
    private fb: FormBuilder,
    private fileService: FileUtilsService,
    private toasterService: ToastService,
    private partnerService: PartnerService,
    public expertsService: ExpertsService,
  ) {
    super(authService);
    this.tagForm = this.fb.group({
      tag: ['', Validators.required],
      image: [null], // <-- added here
      partnerIds: [[]],
      expertIds: [[], Validators.required],
      active: [true],
    });
  }
  updateInterestId: string | undefined = undefined;
  isModalVisible = false;
  tagForm: FormGroup;
  uuidWithoutHyphens = uuidv4().replace(/-/g, '');
  partnerOptions: DropdownOptions[] = [];
  expertOptions: DropdownOptions[] = [];
  existingImageUrl: string | undefined = undefined;
  previewImageUrl: string | undefined = undefined;
  masterDataLoading: boolean = false;
  sessionalTags: SeasonalUserInterestTags[] = [];
  loading: boolean = false;
  async ready(): Promise<void> {
    this.masterDataLoading = true;
    if (this.user) {
      await Promise.all([
        this.getMasterData(),
        this.getPartnerList(),
        this.getExperts(),
      ]);
      this.masterDataLoading = false;
    }
  }

  async getMasterData() {
    this.masterDataService.getSessionalUserInterestTags().subscribe({
      next: (masterData) => {
        if (masterData) {
          this.sessionalTags = masterData;
        }
      },
    });
  }

  async getPartnerList() {
    const request: PartnerGridRequest = {
      page: 1,
      pageSize: 100,
      export: false,
    };
    this.partnerService.partnerList(request).subscribe({
      next: (value) => {
        this.partnerOptions = value.data.map((partner) => ({
          value: partner.id,
          label: partner.partner,
        }));
      },
      error: (error) => {
        const errorBody = error.error as IAnyoError;
        this.toasterService.showAnyoErrorToast(errorBody.description);
      },
    });
  }

  async getExperts() {
    const request: ExpertsGridRequest = {
      page: 1,
      pageSize: 100,
      export: false,
    };
    this.expertsService.listExperts(request).subscribe({
      next: (value) => {
        this.expertOptions = value.data.map((expert) => ({
          value: expert.id!,
          label: expert.name,
        }));
      },
      error: (error) => {
        const errorBody = error.error as IAnyoError;
        this.toasterService.showAnyoErrorToast(errorBody.description);
      },
    });
  }

  onOpenModal() {
    this.tagForm.patchValue({
      tag: '',
      image: '',
      partnerIds: [],
      expertIds: [],
      active: true,
    });
    Object.keys(this.tagForm.controls).forEach((key) => {
      this.tagForm.get(key)?.enable();
    });
    this.isModalVisible = true;
  }

  isEdit: boolean = false;

  onSwitchChange($event: boolean) {
    if (!this.isEdit) {
      return;
    }
    if ($event) {
      this.tagForm.get('partnerIds')?.enable();
      this.tagForm.get('expertIds')?.enable();
      this.tagForm.get('image')?.enable();
    } else {
      this.tagForm.get('partnerIds')?.disable();
      this.tagForm.get('expertIds')?.disable();
      this.tagForm.get('image')?.disable();
    }
  }

  onEditClick(id: string) {
    this.isEdit = true;
    this.updateInterestId = id;
    const tag = this.sessionalTags.find((tag) => tag.m_id === id);
    if (tag) {
      this.existingImageUrl = tag.imageUrl; // ← Save the current image URL
      this.tagForm.patchValue({
        tag: tag.data,
        image: '',
        partnerIds: tag.applicablePartnerIds ?? [],
        expertIds: tag.expertIds ?? [],
        active: tag.active,
      });
      this.tagForm.get('tag')?.disable();
      if (!tag.active) {
        this.tagForm.get('partnerIds')?.disable();
        this.tagForm.get('expertIds')?.disable();
        this.tagForm.get('image')?.disable();
      } else {
        this.tagForm.get('partnerIds')?.enable();
        this.tagForm.get('expertIds')?.enable();
        this.tagForm.get('image')?.enable();
      }
    }
    this.isModalVisible = true;
  }

  uploadImages($event: any) {
    const file: File = $event.target.files[0];
    if (file) {
      this.previewImageUrl = URL.createObjectURL(file);
      this.tagForm.patchValue({ image: file });
      this.tagForm.get('image')?.markAsDirty();
    }
  }

  async handleMultipleFileUpload(file: File) {
    try {
      const extension = file.name.split('.').pop();
      const filePath = `app-assets/concern_images/${this.uuidWithoutHyphens}.${extension}`;
      const uploadUrl = await lastValueFrom(
        this.fileService.generateUploadUrl(
          AnyoS3Buckets.CDN_BUCKET,
          filePath,
          file.type,
        ),
      );
      this.toasterService.showSuccess(`Uploading images`);
      await lastValueFrom(this.fileService.uploadFile(file, uploadUrl.url));
      this.toasterService.showSuccess(`Uploading image completed`);

      return `https://cdn.anyo.app/${filePath}`;
    } catch (e) {
      this.toasterService.showError(e + '');
      throw e;
    }
  }

  async onSubmit() {
    this.loading = true;
    if (this.tagForm.valid) {
      const payload = this.tagForm.getRawValue();
      let image: string = this.existingImageUrl || ''; // ← Start with old image

      if (payload.image) {
        // If new image file selected, handle upload
        image = await this.handleMultipleFileUpload(payload.image);
      }

      const request: AddUserInterestTagsRequest = {
        tag: payload.tag,
        expertIds: payload.expertIds,
        active: payload.active,
        imageUrl: image,
      };

      if (payload.partnerIds.length > 0) {
        request.partnerIds = payload.partnerIds;
      }
      if (this.updateInterestId) {
        request.updateInterestId = this.updateInterestId;
      }

      this.masterDataService.addSessionalUserInterestTags(request).subscribe({
        next: async () => {
          await this.getMasterData();
          const successMessage = this.updateInterestId
            ? 'User interest tag updated successfully.'
            : 'User interest tag added successfully.';
          this.toasterService.showSuccess(successMessage);
          this.isModalVisible = false;
          this.loading = false;
          this.tagForm.reset();
        },
        error: (error) => {
          const errorBody = error.error as IAnyoError;
          this.toasterService.showAnyoErrorToast(errorBody.description);
        },
      });
    }
  }
}
